import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { getMyPermissions, setExpositionMode } from "@/lib/api";
import { useAuth } from "./AuthContext";

const PermissionContext = createContext(null);

export const PermissionProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    if (!user) {
      setPermissions(null);
      // Only mark as done loading if auth itself is done
      // This prevents a window where permLoading=false but auth hasn't resolved yet
      if (!authLoading) {
        setLoading(false);
      }
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      if (token) {
        const response = await getMyPermissions();
        setPermissions(response.data);
        setExpositionMode(response.data?.is_exposition === true);
      }
    } catch (error) {
      // Set default permissions if fetch fails
      setExpositionMode(false);
      setPermissions({
        role: user.role,
        permissions: { sections: {} }
      });
    } finally {
      setLoading(false);
    }
  }, [user, authLoading]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // Check if user has a specific permission
  const hasPermission = useCallback((section, action) => {
    // During loading, assume permission exists to prevent premature redirects
    if (loading) return true;
    
    if (!permissions) return false;
    
    // Only Master Admin has unrestricted access
    if (permissions.is_master_admin) return true;
    
    // Exposition (demo / visual-only) users SEE everything like an admin so all
    // pages and buttons render. Modifications are blocked by the API interceptor.
    if (permissions.is_exposition) return true;
    
    // For Admin and Operational Agent, check actual permissions
    const sectionPerms = permissions.permissions?.sections?.[section];
    if (!sectionPerms) return false;
    
    return sectionPerms[action] === true;
  }, [permissions, loading]);

  // Check if user can access a section (view permission)
  const canAccessSection = useCallback((section) => {
    return hasPermission(section, "view");
  }, [hasPermission]);

  // Refresh permissions (useful after updates)
  const refreshPermissions = useCallback(async () => {
    setLoading(true);
    await fetchPermissions();
  }, [fetchPermissions]);

  return (
    <PermissionContext.Provider value={{
      permissions,
      loading,
      hasPermission,
      canAccessSection,
      refreshPermissions,
      isAdmin: permissions?.role === "admin" || permissions?.role === "master_admin",
      isMasterAdmin: permissions?.is_master_admin === true,
      isExposition: permissions?.is_exposition === true
    }}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error("usePermissions must be used within a PermissionProvider");
  }
  return context;
};
